import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { CalendarDays, Sparkles, Package, Plus, Trash2, Check, X, Users, User, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "bookings", label: "Bookings", icon: CalendarDays },
  { key: "cleanings", label: "Cleaning", icon: Sparkles },
  { key: "inventory", label: "Inventory", icon: Package },
];

const ADD_LABEL = { bookings: "booking", cleanings: "cleaning", inventory: "inventory item" };

export default function Operations() {
  const [tab, setTab] = useState("bookings");
  const [properties, setProperties] = useState([]);
  const [propFilter, setPropFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => { api.get("/properties").then((r) => setProperties(r.data)); }, []);

  const reload = () => setReloadKey((k) => k + 1);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="operations-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Operations</div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900">Run your portfolio</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">Schedule bookings, plan turnovers, and keep every supply closet stocked — all in one place.</p>
        </div>
        <button data-testid="ops-add-btn" onClick={() => setShowAdd(true)}
          disabled={properties.length === 0}
          className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-40 transition">
          <Plus className="w-4 h-4" /> Add {ADD_LABEL[tab]}
        </button>
      </div>

      {properties.length === 0 ? (
        <div className="border-2 border-dashed border-slate-300 rounded-2xl p-16 text-center bg-white">
          <Package className="w-10 h-10 text-slate-300 mx-auto" />
          <div className="mt-4 font-display text-xl text-slate-800">Add a property first</div>
          <p className="text-slate-500 mt-1 text-sm">You need at least one property before you can schedule stays, cleanings, or track inventory.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
              {TABS.map(({ key, label, icon: Icon }) => (
                <button key={key} data-testid={`ops-tab-${key}`} onClick={() => setTab(key)}
                  className={`px-3.5 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2 transition ${tab === key ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
            <select data-testid="ops-property-filter" value={propFilter} onChange={(e) => setPropFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm">
              <option value="">All properties</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {tab === "bookings" && <BookingsTab reloadKey={reloadKey} propFilter={propFilter} onChanged={reload} />}
          {tab === "cleanings" && <CleaningsTab reloadKey={reloadKey} propFilter={propFilter} onChanged={reload} />}
          {tab === "inventory" && <InventoryTab reloadKey={reloadKey} propFilter={propFilter} onChanged={reload} />}
        </>
      )}

      {showAdd && (
        <AddModal tab={tab} properties={properties} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reload(); }} />
      )}
    </div>
  );
}

function useList(path, propFilter, reloadKey) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const q = propFilter ? `?property_id=${propFilter}` : "";
    api.get(`${path}${q}`).then((r) => { setRows(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [path, propFilter, reloadKey]);
  return { rows, loading };
}

function BookingsTab({ reloadKey, propFilter, onChanged }) {
  const { rows, loading } = useList("/bookings", propFilter, reloadKey);
  const remove = async (id) => { await api.delete(`/bookings/${id}`); toast.success("Booking removed"); onChanged(); };
  if (loading) return <Skeleton />;
  if (rows.length === 0) return <EmptyBox msg="No bookings scheduled. Click Add booking to plan a guest stay." />;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {rows.map((b) => (
        <div key={b.id} data-testid={`booking-card-${b.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex gap-4">
          <div className="w-14 shrink-0 text-center border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-900 text-white text-[10px] font-mono uppercase py-1">{formatMonth(b.check_in)}</div>
            <div className="py-2 text-2xl font-display font-bold text-slate-900">{formatDay(b.check_in)}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-semibold text-slate-900 truncate">{b.guest_name}</div>
              <StatusPill status={b.status} />
              <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{b.source}</span>
            </div>
            <div className="text-xs text-slate-500 truncate mt-0.5">{b.property_name} · {b.guests} <Users className="w-3 h-3 inline" /></div>
            <div className="text-xs text-slate-600 mt-1.5"><b>{b.check_in}</b> → <b>{b.check_out}</b> · {nights(b.check_in, b.check_out)} nights</div>
            {b.notes && <div className="text-xs text-slate-500 mt-1 italic line-clamp-2">"{b.notes}"</div>}
          </div>
          <button data-testid={`booking-delete-${b.id}`} onClick={() => remove(b.id)} className="text-slate-400 hover:text-rose-600 self-start p-1">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

function CleaningsTab({ reloadKey, propFilter, onChanged }) {
  const { rows, loading } = useList("/cleanings", propFilter, reloadKey);
  const setStatus = async (c, status) => { await api.patch(`/cleanings/${c.id}`, { status }); toast.success(`Marked ${status}`); onChanged(); };
  const assign = async (c) => {
    const name = window.prompt("Assign cleaner (name):", c.assigned_to || "");
    if (name === null) return;
    await api.patch(`/cleanings/${c.id}`, { assigned_to: name });
    onChanged();
  };
  const remove = async (id) => { await api.delete(`/cleanings/${id}`); onChanged(); };
  if (loading) return <Skeleton />;
  if (rows.length === 0) return <EmptyBox msg="No cleanings scheduled. Turnovers are auto-created when you add a booking, or add one manually." />;
  return (
    <div className="space-y-3">
      {rows.map((c) => (
        <div key={c.id} data-testid={`cleaning-row-${c.id}`} className={`bg-white border rounded-xl p-4 flex items-center gap-4 flex-wrap ${c.status === "completed" ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200"}`}>
          <div className="w-14 shrink-0 text-center border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-sky-500 text-white text-[10px] font-mono uppercase py-1">{formatMonth(c.scheduled_date)}</div>
            <div className="py-1.5 text-xl font-display font-bold text-slate-900">{formatDay(c.scheduled_date)}</div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold text-slate-900">{c.property_name}</div>
              <StatusPill status={c.status} />
              {c.booking_id && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">auto</span>}
            </div>
            <button onClick={() => assign(c)} data-testid={`cleaning-assign-${c.id}`}
              className="text-xs text-slate-600 mt-1 inline-flex items-center gap-1 hover:text-slate-900">
              <User className="w-3 h-3" /> {c.assigned_to || "Unassigned — click to assign"}
            </button>
            {c.notes && <div className="text-xs text-slate-500 mt-1 italic">"{c.notes}"</div>}
          </div>
          <div className="flex items-center gap-1">
            {c.status !== "completed" && (
              <button data-testid={`cleaning-complete-${c.id}`} onClick={() => setStatus(c, "completed")}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Done
              </button>
            )}
            {c.status === "scheduled" && (
              <button onClick={() => setStatus(c, "skipped")} className="px-2 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200">
                Skip
              </button>
            )}
            <button data-testid={`cleaning-delete-${c.id}`} onClick={() => remove(c.id)} className="text-slate-400 hover:text-rose-600 p-1.5">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function InventoryTab({ reloadKey, propFilter, onChanged }) {
  const { rows, loading } = useList("/inventory", propFilter, reloadKey);
  const bump = async (i, delta) => {
    const q = Math.max(0, (i.quantity || 0) + delta);
    await api.patch(`/inventory/${i.id}`, { quantity: q });
    onChanged();
  };
  const remove = async (id) => { await api.delete(`/inventory/${id}`); onChanged(); };
  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) (g[r.property_name || "Unassigned"] ||= []).push(r);
    return g;
  }, [rows]);
  if (loading) return <Skeleton />;
  if (rows.length === 0) return <EmptyBox msg="No inventory yet. Track towels, coffee, batteries — whatever guests eat through." />;
  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([pname, items]) => (
        <div key={pname}>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">{pname}</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((i) => (
              <div key={i.id} data-testid={`inventory-item-${i.id}`} className={`bg-white border rounded-xl p-4 ${i.low_stock ? "border-rose-200" : "border-slate-200"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{i.item}</div>
                    <div className="text-[10px] font-mono uppercase text-slate-500 mt-0.5">{i.category}</div>
                  </div>
                  {i.low_stock && <span data-testid={`inventory-low-${i.id}`} className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Low</span>}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <button data-testid={`inventory-minus-${i.id}`} onClick={() => bump(i, -1)} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-100">−</button>
                    <div className="w-14 text-center">
                      <div className="text-xl font-display font-bold text-slate-900">{i.quantity}</div>
                      <div className="text-[10px] font-mono text-slate-500">of {i.threshold} min {i.unit && `· ${i.unit}`}</div>
                    </div>
                    <button data-testid={`inventory-plus-${i.id}`} onClick={() => bump(i, +1)} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-100">+</button>
                  </div>
                  <button onClick={() => remove(i.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AddModal({ tab, properties, onClose, onSaved }) {
  const [form, setForm] = useState({ property_id: properties[0]?.id || "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (tab === "bookings") await api.post("/bookings", { ...form, guests: Number(form.guests || 1) });
      if (tab === "cleanings") await api.post("/cleanings", form);
      if (tab === "inventory") await api.post("/inventory", { ...form, quantity: Number(form.quantity || 0), threshold: Number(form.threshold || 0) });
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-display font-bold text-slate-900">Add {ADD_LABEL[tab]}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Select label="Property" testid="ops-form-property" value={form.property_id} onChange={(v) => set("property_id", v)} required>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          {tab === "bookings" && <>
            <Input label="Guest name" testid="ops-form-guest" value={form.guest_name || ""} onChange={(v) => set("guest_name", v)} required />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Check-in" type="date" testid="ops-form-checkin" value={form.check_in || ""} onChange={(v) => set("check_in", v)} required />
              <Input label="Check-out" type="date" testid="ops-form-checkout" value={form.check_out || ""} onChange={(v) => set("check_out", v)} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select label="Source" testid="ops-form-source" value={form.source || "airbnb"} onChange={(v) => set("source", v)}>
                <option>airbnb</option><option>vrbo</option><option>booking</option><option>direct</option><option>other</option>
              </Select>
              <Input label="Guests" type="number" min={1} value={form.guests || 1} onChange={(v) => set("guests", v)} />
            </div>
          </>}
          {tab === "cleanings" && <>
            <Input label="Scheduled date" type="date" testid="ops-form-date" value={form.scheduled_date || ""} onChange={(v) => set("scheduled_date", v)} required />
            <Input label="Assigned to" value={form.assigned_to || ""} onChange={(v) => set("assigned_to", v)} placeholder="e.g. Maria's Cleaning Co" />
          </>}
          {tab === "inventory" && <>
            <Input label="Item" testid="ops-form-item" value={form.item || ""} onChange={(v) => set("item", v)} required />
            <div className="grid grid-cols-2 gap-3">
              <Select label="Category" value={form.category || "supplies"} onChange={(v) => set("category", v)}>
                <option>linens</option><option>toiletries</option><option>kitchen</option><option>supplies</option><option>other</option>
              </Select>
              <Input label="Unit" value={form.unit || ""} onChange={(v) => set("unit", v)} placeholder="rolls, sets" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Quantity" type="number" min={0} testid="ops-form-qty" value={form.quantity || 0} onChange={(v) => set("quantity", v)} />
              <Input label="Reorder at" type="number" min={0} value={form.threshold || 0} onChange={(v) => set("threshold", v)} />
            </div>
          </>}
          <button disabled={saving} data-testid="ops-form-submit" className="w-full bg-slate-900 text-white py-3 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50 transition">
            {saving ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, ...rest }) {
  return (
    <label className="block text-sm">
      <span className="text-slate-700 font-medium">{label}</span>
      <input {...rest} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-sky-500 outline-none" />
    </label>
  );
}
function Select({ label, value, onChange, testid, children, ...rest }) {
  return (
    <label className="block text-sm">
      <span className="text-slate-700 font-medium">{label}</span>
      <select {...rest} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-sky-500 outline-none">
        {children}
      </select>
    </label>
  );
}

function StatusPill({ status }) {
  const map = {
    confirmed: "bg-sky-50 text-sky-700 border-sky-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-slate-100 text-slate-500 border-slate-200",
    scheduled: "bg-amber-50 text-amber-700 border-amber-200",
    skipped: "bg-slate-100 text-slate-500 border-slate-200",
  };
  return <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${map[status] || map.scheduled}`}>{status}</span>;
}

function EmptyBox({ msg }) {
  return <div className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center bg-white text-slate-500 text-sm">{msg}</div>;
}
function Skeleton() {
  return <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-xl skeleton" />)}</div>;
}

const formatMonth = (d) => new Date(d + "T00:00:00").toLocaleString("en-US", { month: "short" });
const formatDay = (d) => new Date(d + "T00:00:00").getDate();
const nights = (a, b) => Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000));
